import React from 'react';
import { Typography, Box, List, ListItem, Divider, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import NavigatorLink from 'views/rebranded-module/common/components/Logical/NavigatorLink';
import StyledBox,{
  StyledCodeInline,
  StyledBlockquote,
  StyledCodeBlock,
  StyledImage,
  StyledRelationship,
} from 'views/rebranded-module/common/components/UI/RichTextRenderer/RichTextRenderer.styles';
import { Typography700 } from 'views/rebranded-module/common/components/UI/Typography700/Typography700';
import MuiLink from '@mui/material/Link';

const RichTextRenderer = ({ content }) => {
  if (!content) return null;

  const rootContent = content.root
    ? content.root.children
    : Array.isArray(content)
    ? content
    : [content];

  // Helper function to check if a paragraph is empty
  const isParagraphEmpty = (node) => {
    if (!node.children || node.children.length === 0) return true;
    
    // Check if all children are empty text nodes
    return node.children.every(child => {
      if (child.type === 'text') {
        return !child.text || child.text.trim() === '';
      }
      return false;
    });
  };

  const renderNode = (node, index) => {
    if (!node) return null;

    if (node.type === 'text') {
      let textElement = node.text;
      const format = node.format || 0;

      // Preserve whitespace by using non-breaking spaces for leading/trailing spaces
      if (textElement) {
        // Replace leading spaces with non-breaking spaces
        textElement = textElement.replace(/^(\s+)/, (match) => 
          '\u00A0'.repeat(match.length)
        );
        // Replace trailing spaces with non-breaking spaces
        textElement = textElement.replace(/(\s+)$/, (match) => 
          '\u00A0'.repeat(match.length)
        );
        // Replace multiple consecutive spaces with non-breaking spaces
        textElement = textElement.replace(/(\s{2,})/g, (match) => 
          '\u00A0'.repeat(match.length)
        );
      }

      // Handle line breaks in text content
      if (textElement && textElement.includes('\n')) {
        const lines = textElement.split('\n');
        textElement = lines.map((line, lineIndex) => (
          <React.Fragment key={`line-${index}-${lineIndex}`}>
            {line}
            {lineIndex < lines.length - 1 && <br />}
          </React.Fragment>
        ));
      }

      if (format & 1) {
        textElement = (
          <Typography700 component="span" key={`bold-${index}`}>
            {textElement}
          </Typography700>
        );
      }
      if (format & 2) {
        textElement = <em key={`italic-${index}`}>{textElement}</em>;
      }
      if (format & 8) {
        textElement = <u key={`underline-${index}`}>{textElement}</u>;
      }
      if (format & 4) {
        textElement = <s key={`strike-${index}`}>{textElement}</s>;
      }
      if (format & 16) {
        textElement = (
          <StyledCodeInline key={`code-${index}`}>
            {textElement}
          </StyledCodeInline>
        );
      }

      return textElement;
    }

    switch (node.type) {
      case 'linebreak':
      case 'break':
        return <br key={index} />;

      case 'heading':
        const HeadingTag = node.tag || 'h1';
        const variantMap = {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
        };

        return (
          <Typography
            key={index}
            variant={variantMap[HeadingTag] || 'h1'}
            component={HeadingTag}>
            {node.children?.map((child, childIndex) =>
              renderNode(child, `${index}-${childIndex}`),
            )}
          </Typography>
        );

      case 'paragraph':
        // Check if paragraph is empty and render a line break instead
        if (isParagraphEmpty(node)) {
          // Option 1: Render just a line break
          return <br key={index} />;
          
          // Option 2: Render an empty paragraph with minimal height
          // return (
          //   <Typography 
          //     key={index} 
          //     variant="body1" 
          //     component="p" 
          //     sx={{ minHeight: '1.2em', margin: 0 }}
          //   >
          //     &nbsp;
          //   </Typography>
          // );
          
          // Option 3: Render a div with specific height
          // return <Box key={index} sx={{ height: '1.2em' }} />;
        }

        return (
          <Typography 
            key={index} 
            variant="body1" 
            component="p"
            
          >
            {node.children?.map((child, childIndex) =>
              renderNode(child, `${index}-${childIndex}`),
            )}
          </Typography>
        );

      case 'list':
        const ListTag =
          node.tag === 'ol' || node.listType === 'number' ? 'ol' : 'ul';

        return React.createElement(
          ListTag,
          { key: index, className: 'RTRList' },
          node.children?.map((child, childIndex) =>
            renderNode(child, `${index}-${childIndex}`),
          ),
        );

      case 'listitem':
        return (
          <li
            key={index}
            className="RTRListItem">
            <Typography variant="body1" mb={`0 !important`}>
              {node.children?.map((child, childIndex) =>
                renderNode(child, `${index}-${childIndex}`),
              )}
            </Typography>
          </li>
        );

      case 'link':
        const url = node.fields?.url || node.url;
        const newTab = node.fields?.newTab || node.newTab;

        return (
          <>
            {url.startsWith('http') || url.startsWith('/') ? (
              <NavigatorLink
                key={index}
                href={url}
                target={newTab ? '_blank' : '_self'}
                rel={newTab ? 'noopener noreferrer' : undefined}
                color="primary"
                underline="hover">
                {node.children?.map((child, childIndex) =>
                  renderNode(child, `${index}-${childIndex}`),
                )}
              </NavigatorLink>
            ) : (
              <MuiLink
                href={url}
                target={newTab ? '_blank' : '_self'}
                rel={newTab ? 'noopener noreferrer' : undefined}
                color="primary"
                underline="hover">
                {node.children?.map((child, childIndex) =>
                  renderNode(child, `${index}-${childIndex}`),
                )}{' '}
              </MuiLink>
            )}
          </>
        );

      case 'blockquote':
        return (
          <StyledBlockquote key={index}>
            <Typography variant="body1" component="blockquote">
              {node.children?.map((child, childIndex) =>
                renderNode(child, `${index}-${childIndex}`),
              )}
            </Typography>
          </StyledBlockquote>
        );

      case 'code':
        return (
          <StyledCodeBlock key={index}>
            <code>
              {node.children?.map((child, childIndex) =>
                renderNode(child, `${index}-${childIndex}`),
              )}
            </code>
          </StyledCodeBlock>
        );

      case 'upload':
        if (node.value && typeof node.value === 'object') {
          return (
            <StyledImage
              key={index}
              src={node.value.url}
              alt={node.value.alt || ''}
            />
          );
        }
        return null;

      case 'relationship':
        if (node.value && typeof node.value === 'object') {
          return (
            <StyledRelationship key={index}>
              <Typography variant="body2" color="textSecondary">
                Related content: {node.value.title || node.value.id}
              </Typography>
            </StyledRelationship>
          );
        }
        return null;

      case 'hr':
        return <Divider key={index} sx={{ my: 4 }} />;

      case 'br':
        return <br key={index} />;

      default:
        if (node.children) {
          return (
            <Box key={index}>
              {node.children.map((child, childIndex) => {
                return <Box>{renderNode(child, `${index}-${childIndex}`)}</Box>;
              })}
            </Box>
          );
        }
        return null;
    }
  };

  return <StyledBox>{rootContent.map((node, index) => renderNode(node, index))}</StyledBox>;
};

export { RichTextRenderer };